// Pure helpers of the sharing screens (COL-FE-02 and COL-FE-03): scopes, the share form and its
// checks, statuses, the rows the lists show and plain-language error messages.
import type {
  IncomingSpaceInvitationListDtoOutputItemsItem,
  InvitePartnerDto,
  OrgPartnershipListDtoOutputItemsItem,
  SpaceApprovalListDtoOutputItemsItem,
  SpaceAuditPageDtoOutputItemsItem,
  SpaceInvitationListDtoOutputItemsItem,
  SpaceMemberListDtoOutputItemsItem,
  SpacePartnerListDtoOutputItemsItem,
} from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { formatDate, formatDateTime, type AppLocale } from '@batiflow/i18n';
import type { StatusIntent } from '@batiflow/ui';
import type { TFunction } from 'i18next';
import { todayInTunis } from '@/features/admin/fiscal/model';
import type { Translate } from '../model';

export type SpacePartner = SpacePartnerListDtoOutputItemsItem;
export type SpaceMember = SpaceMemberListDtoOutputItemsItem;
export type SpaceInvitation = SpaceInvitationListDtoOutputItemsItem;
export type IncomingInvitation = IncomingSpaceInvitationListDtoOutputItemsItem;
export type Partnership = OrgPartnershipListDtoOutputItemsItem;
export type SpaceApproval = SpaceApprovalListDtoOutputItemsItem;
export type SpaceAuditEvent = SpaceAuditPageDtoOutputItemsItem;

export type Scope = SpacePartner['scope'];
export type Rights = SpacePartner['rights'];

export const RIGHTS_ORDER = ['read', 'write', 'approve'] as const satisfies readonly Rights[];

/** Rights up to `max`: a delegate never gets more than the partnership. */
export const rightsUpTo = (max: Rights): Rights[] =>
  RIGHTS_ORDER.slice(0, RIGHTS_ORDER.indexOf(max) + 1);

export const isRights = (value: string): value is Rights =>
  (RIGHTS_ORDER as readonly string[]).includes(value);

export const isWholeScope = (scope: Scope): scope is { all: true } => 'all' in scope;

export const MEMBER_CAP_DEFAULT = 5;
export const MEMBER_CAP_MAX = 500;

// --- Scope --------------------------------------------------------------------------------------

/** What the scope picker edits: the whole space, or some folders and channels. */
export interface ScopeDraft {
  whole: boolean;
  folderIds: string[];
  channelIds: string[];
}

export const WHOLE_SCOPE_DRAFT: ScopeDraft = { whole: true, folderIds: [], channelIds: [] };
export const EMPTY_LIMITED_DRAFT: ScopeDraft = { whole: false, folderIds: [], channelIds: [] };

/** The API scope of a draft, or null when a limited scope has nothing chosen. */
export function scopeOfDraft(draft: ScopeDraft): Scope | null {
  if (draft.whole) return { all: true };
  if (draft.folderIds.length + draft.channelIds.length === 0) return null;
  return { folderIds: [...draft.folderIds], channelIds: [...draft.channelIds] };
}

/** "Tout l'espace" or "Dossiers : 2 · Canaux : 1". */
export function scopeSummary(scope: Scope, t: Translate): string {
  if (isWholeScope(scope)) return t('sharing.scope.summaryAll');
  return t('sharing.scope.summarySome', {
    folders: scope.folderIds.length,
    channels: scope.channelIds.length,
  });
}

/** A copy of an object without one key (clears the error of a field). */
export function withoutKey<T extends object, K extends keyof T>(value: T, key: K): Omit<T, K> {
  return Object.fromEntries(Object.entries(value).filter(([name]) => name !== key)) as Omit<T, K>;
}

/** Adds or removes an id in a list (a new list is returned). */
export const toggled = (ids: readonly string[], id: string, on: boolean): string[] =>
  on ? (ids.includes(id) ? [...ids] : [...ids, id]) : ids.filter((value) => value !== id);

// --- Statuses -----------------------------------------------------------------------------------

export const PARTNER_STATUS_INTENT: Record<SpacePartner['status'], StatusIntent> = {
  pending: 'warning',
  active: 'success',
  declined: 'neutral',
  expired: 'neutral',
  revoked: 'danger',
};

export const INVITATION_STATUS_INTENT: Record<SpaceInvitation['status'], StatusIntent> = {
  pending_approval: 'warning',
  pending: 'informative',
  accepted: 'success',
  declined: 'neutral',
  expired: 'neutral',
  revoked: 'danger',
};

/** Invitations still waiting for an answer: the ones the host can cancel. */
export const isOpenInvitation = (invitation: Pick<SpaceInvitation, 'status'>): boolean =>
  invitation.status === 'pending' || invitation.status === 'pending_approval';

/** Partners the host shows first: waiting ones, then active ones, then the ended ones. */
export function sortPartners(items: readonly SpacePartner[]): SpacePartner[] {
  const rank = (partner: SpacePartner): number =>
    partner.status === 'pending' ? 0 : partner.status === 'active' ? 1 : 2;
  return [...items].sort(
    (a, b) => rank(a) - rank(b) || a.organizationName.localeCompare(b.organizationName),
  );
}

/** Partners the host can still remove (a pending request is withdrawn the same way). */
export const canRemovePartner = (partner: Pick<SpacePartner, 'status'>): boolean =>
  partner.status === 'active' || partner.status === 'pending';

// --- Dates --------------------------------------------------------------------------------------

/** Tunisia has no daylight saving time: Africa/Tunis is UTC+01:00 all year. */
const TUNIS_OFFSET = '+01:00';

/** The end of a Tunisian calendar day (`YYYY-MM-DD`) as an ISO instant. */
export const endOfDayInTunis = (date: string): string =>
  new Date(`${date}T23:59:59${TUNIS_OFFSET}`).toISOString();

/** Left-to-right isolate and its end: a date keeps its order inside an Arabic sentence. */
const LRI = String.fromCharCode(0x2066);
const PDI = String.fromCharCode(0x2069);

export const dateText = (iso: string, locale: AppLocale): string =>
  `${LRI}${formatDate(iso, locale)}${PDI}`;

export const dateTimeText = (iso: string, locale: AppLocale): string =>
  `${LRI}${formatDateTime(iso, locale)}${PDI}`;

export const DAY_MS = 24 * 3600 * 1000;

/** Whole days from now to `iso` (rounded up; negative once past). */
export const daysUntil = (iso: string, now: Date): number =>
  Math.ceil((new Date(iso).getTime() - now.getTime()) / DAY_MS);

/** A guest access that ends within this many days is shown with the warning style. */
export const EXPIRY_WARNING_DAYS = 14;

// --- Share form ---------------------------------------------------------------------------------

export interface ShareValues {
  /** The normalised matricule fiscal that was looked up and confirmed. */
  taxId: string | null;
  /** The organisation the lookup found for `taxId` (the person confirmed its name). */
  organizationName: string | null;
  /** The organisation chosen in the partner directory (the invitation then sends its id, not a tax id). */
  directoryId: string | null;
  scope: ScopeDraft;
  rights: Rights;
  memberCap: string;
  /** `YYYY-MM-DD` or empty (until revoked). */
  endsOn: string;
}

export const INITIAL_SHARE_VALUES: ShareValues = {
  taxId: null,
  organizationName: null,
  directoryId: null,
  scope: WHOLE_SCOPE_DRAFT,
  rights: 'write',
  memberCap: String(MEMBER_CAP_DEFAULT),
  endsOn: '',
};

export type ShareField = 'taxId' | 'scope' | 'memberCap' | 'endsOn';
export type ShareErrors = Partial<Record<ShareField, string>>;

export type ShareResult = { ok: true; data: InvitePartnerDto } | { ok: false; errors: ShareErrors };

/** The POST body of the share dialog, or what is wrong with the form (in plain language). */
export function buildPartnerInvite(values: ShareValues, t: Translate, now: Date): ShareResult {
  const errors: ShareErrors = {};
  if (values.directoryId !== null) {
    // Chosen in the directory: no tax id needed.
  } else if (values.taxId === null) errors.taxId = t('sharing.share.errors.taxIdRequired');
  else if (values.organizationName === null) errors.taxId = t('sharing.share.errors.notConfirmed');
  const scope = scopeOfDraft(values.scope);
  if (scope === null) errors.scope = t('sharing.scope.errors.empty');
  const cap = Number(values.memberCap);
  if (!/^\d+$/.test(values.memberCap.trim()) || cap < 1 || cap > MEMBER_CAP_MAX) {
    errors.memberCap = t('sharing.share.errors.capInvalid', { max: MEMBER_CAP_MAX });
  }
  if (values.endsOn !== '' && values.endsOn < todayInTunis(now)) {
    errors.endsOn = t('sharing.share.errors.endPast');
  }
  const target =
    values.directoryId !== null
      ? { organizationId: values.directoryId }
      : values.taxId !== null
        ? { taxId: values.taxId }
        : null;
  if (Object.keys(errors).length > 0 || target === null || scope === null) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    data: {
      ...target,
      scope,
      rights: values.rights,
      memberCap: cap,
      expiresAt: values.endsOn === '' ? null : endOfDayInTunis(values.endsOn),
    },
  };
}

// --- Invite a person ----------------------------------------------------------------------------

export type PersonKind = 'nominative' | 'guest';

export interface PersonValues {
  kind: PersonKind;
  email: string;
  scope: ScopeDraft;
  rights: Rights;
  endsOn: string;
}

export const INITIAL_PERSON_VALUES: PersonValues = {
  kind: 'nominative',
  email: '',
  scope: WHOLE_SCOPE_DRAFT,
  rights: 'write',
  endsOn: '',
};

/** A simple shape check; the server decides which addresses are valid. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export type PersonField = 'email' | 'scope' | 'endsOn';
export type PersonErrors = Partial<Record<PersonField, string>>;

export type PersonResult =
  | {
      ok: true;
      data: {
        kind: PersonKind;
        email: string;
        scope: Scope;
        rights: Rights;
        accessExpiresAt?: string | null;
      };
    }
  | { ok: false; errors: PersonErrors };

export function buildPersonInvite(values: PersonValues, t: Translate, now: Date): PersonResult {
  const errors: PersonErrors = {};
  const email = values.email.trim();
  if (email === '') errors.email = t('sharing.person.errors.emailRequired');
  else if (!EMAIL_SHAPE.test(email) || email.length > 320) {
    errors.email = t('sharing.person.errors.emailInvalid');
  }
  // A guest is always limited to some folders or channels; a named person may have the whole space.
  const draft = values.kind === 'guest' ? { ...values.scope, whole: false } : values.scope;
  const scope = scopeOfDraft(draft);
  if (scope === null) errors.scope = t('sharing.scope.errors.empty');
  if (values.kind === 'nominative' && values.endsOn !== '' && values.endsOn < todayInTunis(now)) {
    errors.endsOn = t('sharing.share.errors.endPast');
  }
  if (Object.keys(errors).length > 0 || scope === null) return { ok: false, errors };
  return {
    ok: true,
    data: {
      kind: values.kind,
      email,
      scope,
      rights: values.rights,
      ...(values.kind === 'nominative'
        ? { accessExpiresAt: values.endsOn === '' ? null : endOfDayInTunis(values.endsOn) }
        : {}),
    },
  };
}

// --- People -------------------------------------------------------------------------------------

/** A guest's access can be extended by 90 days by the people who manage the space. */
export const canExtend = (member: Pick<SpaceMember, 'via'>): boolean => member.via === 'guest';

export const canRevokeMember = (member: Pick<SpaceMember, '_permissions'>): boolean =>
  member._permissions.includes('can_revoke');

// --- Audit --------------------------------------------------------------------------------------

export const AUDIT_PAGE_SIZE = 50;

const AUDIT_ACTIONS = [
  'space.created',
  'space.updated',
  'space.closed',
  'space.partner_invited',
  'space.partner_accepted',
  'space.partner_declined',
  'space.partner_revoked',
  'space.partner_expired',
  'space.member_delegated',
  'space.member_joined',
  'space.member_revoked',
  'space.member_expired',
  'space.member_extended',
  'space.invitation_created',
  'space.invitation_declined',
  'space.invitation_revoked',
  'space.approval_decided',
  // Tenant-level events the organisation audit shares with the space audit (ORGX-FE-02).
  'organization.directory_updated',
  'directory.contact_received',
] as const;

/** The key (under `sharing.audit.actions`) of an action that has its own sentence, else null. */
export function auditActionKey(action: string): string | null {
  const known = AUDIT_ACTIONS.find((candidate) => candidate === action);
  return known ? known.slice(known.indexOf('.') + 1) : null;
}

// --- Errors -------------------------------------------------------------------------------------

export const errorCodeOf = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;

/**
 * A plain-language message for a refused sharing action: the code's own sentence (the `errors`
 * map of the `common` namespace), a context sentence for a refusal, else the generic one. Never the
 * server's raw text.
 */
export function sharingErrorMessage(
  t: Translate,
  tCommon: TFunction<'common'>,
  error: unknown,
  context: { forbidden: string; planLimit?: string },
): string {
  if (error instanceof ApiError) {
    if (error.code === 'FORBIDDEN') return t(context.forbidden);
    if (error.code === 'PLAN_LIMIT_REACHED' && context.planLimit) return t(context.planLimit);
    return tCommon(`errors.${error.code}`, { defaultValue: tCommon('errors.generic') });
  }
  return tCommon('errors.generic');
}
